import React, { useEffect, useRef, useState } from "react";
import { MapPin, Phone, Mail } from "lucide-react";
import { motion } from "framer-motion";

const CONTACT_API_URL =
  process.env.REACT_APP_CONTACT_API_URL || "https://api.cloudvetx.com/contact";

const FIELD_ERROR_IDS = {
  name: "contact-name-error",
  clinicName: "contact-clinic-name-error",
  email: "contact-email-error",
  phone: "contact-phone-error",
  message: "contact-message-error",
};

const SUBMIT_ERROR_ID = "contact-submit-error";

const ContactForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    clinicName: "",
    email: "",
    phone: "",
    message: "",
    website: "",
  });

  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const successHeadingRef = useRef(null);

  useEffect(() => {
    if (submitted) {
      successHeadingRef.current?.focus({ preventScroll: true });
    }
  }, [submitted]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    if (submitError) {
      setSubmitError("");
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.clinicName.trim()) {
      newErrors.clinicName = "Clinic name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    }

    if (!formData.message.trim()) {
      newErrors.message = "Message is required";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSubmitError("");

    const newErrors = validateForm();

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(CONTACT_API_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          clinicName: formData.clinicName.trim(),
          email: formData.email.trim(),
          phone: formData.phone.trim(),
          message: formData.message.trim(),
          website: formData.website.trim(),
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        if (result?.errors && typeof result.errors === "object") {
          setErrors(result.errors);
        }

        setSubmitError(
          result?.message || "Unable to send your message right now."
        );
        return;
      }

      setSubmitted(true);
      setErrors({});
      setSubmitError("");
      setFormData({
        name: "",
        clinicName: "",
        email: "",
        phone: "",
        message: "",
        website: "",
      });
    } catch (error) {
      console.error("Form submission error:", error);
      setSubmitError(
        "A network error occurred. Please try again in a moment."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section id="contact" className="cvx-section cvx-contact">
        <div className="container mx-auto px-5 sm:px-8">
          <motion.div
            className="contact-form cvx-contact-success text-center"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <div className="mb-6" style={{ color: "var(--accent-primary)" }}>
              <svg
                className="w-16 h-16 mx-auto"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h3
              ref={successHeadingRef}
              id="contact-success-heading"
              tabIndex={-1}
              aria-describedby="contact-success-message"
              className="heading-3 mb-4"
            >
              Thank You!
            </h3>
            <p
              id="contact-success-message"
              className="body-medium text-gray-600 mb-6"
            >
              We&apos;ve received your enquiry. Our team will be in touch.
            </p>
            <motion.button
              onClick={() => setSubmitted(false)}
              className="btn-secondary"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Send Another Message
            </motion.button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="contact" className="cvx-section cvx-contact overflow-hidden">
      <div className="container mx-auto px-5 sm:px-8">
        <motion.div
          className="cvx-contact-intro"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="cvx-section-title">Interested in the Founders Pilot?</h2>
          <p className="body-large text-gray-600">
            Tell us about your clinic. We can arrange a product walkthrough and
            discuss whether the pilot is a fit for your team.
          </p>
        </motion.div>

        <div className="cvx-contact-layout">
          <motion.div
            className="contact-form"
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <form
              onSubmit={handleSubmit}
              className="cvx-contact-fields"
              noValidate
            >
              <input
                type="text"
                name="website"
                value={formData.website}
                onChange={handleChange}
                autoComplete="off"
                tabIndex="-1"
                className="hidden"
                aria-hidden="true"
              />

              {submitError && (
                <div
                  id={SUBMIT_ERROR_ID}
                  role="alert"
                  className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                >
                  {submitError}
                </div>
              )}

              <div className="cvx-contact-field-pair cvx-contact-clinic">
              {[
                { id: "name", label: "Full Name *", type: "text" },
                { id: "clinicName", label: "Clinic Name *", type: "text" },
              ].map((field) => (
                <div key={field.id}>
                  <label
                    htmlFor={field.id}
                    className="block body-medium font-medium mb-2"
                  >
                    {field.label}
                  </label>
                  <motion.input
                    type={field.type}
                    id={field.id}
                    name={field.id}
                    value={formData[field.id]}
                    onChange={handleChange}
                    required
                    aria-invalid={Boolean(errors[field.id])}
                    aria-describedby={
                      errors[field.id] ? FIELD_ERROR_IDS[field.id] : undefined
                    }
                    className={`form-input ${
                      errors[field.id] ? "border-red-500" : ""
                    }`}
                    whileFocus={{ scale: 1.01 }}
                  />
                  {errors[field.id] && (
                    <p
                      id={FIELD_ERROR_IDS[field.id]}
                      className="text-red-500 text-sm mt-1"
                    >
                      {errors[field.id]}
                    </p>
                  )}
                </div>
              ))}
              </div>

              <div className="cvx-contact-field-pair cvx-contact-details">
                <div>
                  <label
                    htmlFor="email"
                    className="block body-medium font-medium mb-2"
                  >
                    Email Address *
                  </label>
                  <motion.input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    aria-invalid={Boolean(errors.email)}
                    aria-describedby={
                      errors.email ? FIELD_ERROR_IDS.email : undefined
                    }
                    className={`form-input ${
                      errors.email ? "border-red-500" : ""
                    }`}
                    whileFocus={{ scale: 1.01 }}
                  />
                  {errors.email && (
                    <p
                      id={FIELD_ERROR_IDS.email}
                      className="text-red-500 text-sm mt-1"
                    >
                      {errors.email}
                    </p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="phone"
                    className="block body-medium font-medium mb-2"
                  >
                    Phone Number *
                  </label>
                  <motion.input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    aria-invalid={Boolean(errors.phone)}
                    aria-describedby={
                      errors.phone ? FIELD_ERROR_IDS.phone : undefined
                    }
                    className={`form-input ${
                      errors.phone ? "border-red-500" : ""
                    }`}
                    whileFocus={{ scale: 1.01 }}
                  />
                  {errors.phone && (
                    <p
                      id={FIELD_ERROR_IDS.phone}
                      className="text-red-500 text-sm mt-1"
                    >
                      {errors.phone}
                    </p>
                  )}
                </div>
              </div>

              <div className="cvx-contact-message">
                <label
                  htmlFor="message"
                  className="block body-medium font-medium mb-2"
                >
                  Message *
                </label>
                <motion.textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  aria-invalid={Boolean(errors.message)}
                  aria-describedby={
                    errors.message ? FIELD_ERROR_IDS.message : undefined
                  }
                  className={`form-textarea ${
                    errors.message ? "border-red-500" : ""
                  }`}
                  rows={5}
                  whileFocus={{ scale: 1.01 }}
                />
                {errors.message && (
                  <p
                    id={FIELD_ERROR_IDS.message}
                    className="text-red-500 text-sm mt-1"
                  >
                    {errors.message}
                  </p>
                )}
              </div>

              <p className="cvx-contact-privacy body-small text-gray-600 leading-relaxed">
                We use your contact details and message to respond to your enquiry
                and discuss the pilot. Please do not include identifiable client
                records, clinical recordings or card details. Read our{" "}
                <a href="/privacy-policy" className="underline text-emerald-800">
                  Privacy Policy
                </a>{" "}
                for how we handle this information and your rights.
              </p>

              <motion.button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full disabled:opacity-70 disabled:cursor-not-allowed"
                whileHover={isSubmitting ? {} : { scale: 1.05 }}
                whileTap={isSubmitting ? {} : { scale: 0.95 }}
              >
                {isSubmitting ? "Sending..." : "Ask About the Pilot"}
              </motion.button>
            </form>
          </motion.div>

          <motion.div
            className="cvx-contact-support"
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <div>
              <h3 className="heading-3 cvx-contact-support-title">Get in Touch</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <div
                    className="p-3 rounded-lg"
                    style={{ backgroundColor: "var(--accent-wash)" }}
                  >
                    <Phone
                      size={24}
                      style={{ color: "var(--accent-primary)" }}
                    />
                  </div>
                  <div>
                    <div className="font-medium">Phone</div>
                    <div className="body-small text-gray-600">
                      +971 (52) 342-5782
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div
                    className="p-3 rounded-lg"
                    style={{ backgroundColor: "var(--accent-wash)" }}
                  >
                    <Mail
                      size={24}
                      style={{ color: "var(--accent-primary)" }}
                    />
                  </div>
                  <div>
                    <div className="font-medium">Email</div>
                    <div className="body-small text-gray-600">
                      Info@cloudvetx.com
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div
                    className="p-3 rounded-lg"
                    style={{ backgroundColor: "var(--accent-wash)" }}
                  >
                    <MapPin
                      size={24}
                      style={{ color: "var(--accent-primary)" }}
                    />
                  </div>
                  <div>
                    <div className="font-medium">Office</div>
                    <div className="body-small text-gray-600">
                      Dubai, Dubai, United Arab Emirates
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="cvx-contact-expect"
            >
              <h4 className="font-semibold mb-3">What to expect</h4>
              <ul className="space-y-2 body-small">
                <li>• A conversation about your clinic's workflow</li>
                <li>• A walkthrough of the relevant product features</li>
                <li>• A discussion of setup and migration needs</li>
                <li>• Clear pilot scope agreed before you join</li>
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
